/**
 * The candidate loader every attribution picker uses.
 *
 * One loader rather than one per surface, because the loader is where the
 * eligibility rule is *explained*: rows that cannot legally land on the subject
 * come back marked `ineligible` — greyed, with the reason — instead of being
 * filtered out, which would leave an operator searching for a label that is
 * right there in the catalog and silently absent from the list. Seven surfaces
 * built their own `catalog.list` mapper and none of them did that.
 *
 * The mapping and the rule are `attributionCandidateLoader` in
 * `@authe/fe-commons/attribution`, shared with the admin portal. What is left
 * here is the catalog client and the identity stability, which is load-bearing
 * rather than a micro-optimisation: `CatalogItemPicker` re-runs its search
 * effect whenever `loadCandidates` changes identity, and the search sets state —
 * so a loader rebuilt each render fetches, re-renders, and fetches again for as
 * long as the popover is open.
 *
 * The rule is enforced again in `useAttributionController`, from the same
 * candidate row. This half is the courtesy; that half is the guarantee.
 */
import * as React from "react";
import type { AttributionEditorCandidate } from "@authe/ui-core";
import { useCatalogApi } from "@/api/adapters";
import {
  ATTRIBUTION_KINDS,
  attributionCandidateLoader,
  type AttributionKind,
  type AttributionSubject,
} from "@authe/fe-commons/attribution";

/** Loads name/alias matches of one kind. The editor's `loadCandidates`. */
export type CatalogCandidateLoader = (
  kind: AttributionKind,
  search: string,
) => Promise<AttributionEditorCandidate[]>;

/** One kind's loader, for a surface driving `CatalogItemPicker` directly. */
export type CatalogLoader = (search: string) => Promise<AttributionEditorCandidate[]>;

/**
 * @param subject The thing a picked row will be attached to. Omit for a picker
 *   with no single subject — the bulk bar before a target is chosen — and rows
 *   come back unjudged.
 */
export function useCatalogCandidates(
  subject?: AttributionSubject,
): CatalogCandidateLoader {
  const catalog = useCatalogApi();
  // The subject's identity, not the object: callers build the literal inline, so
  // depending on the object itself would rebuild the loader on every render —
  // exactly the refetch loop described above.
  const subjectKey = subject ? JSON.stringify(subject) : "";

  return React.useCallback(
    (kind: AttributionKind, search: string) => {
      const target = subjectKey ? (JSON.parse(subjectKey) as AttributionSubject) : undefined;
      return attributionCandidateLoader(
        // A picker wants the first page and nothing else; the cursor is dropped
        // on purpose, because the way to see further here is to keep typing.
        async (k, query) => (await catalog.list(k, query)).items,
        target,
      )(kind, search);
    },
    [catalog, subjectKey],
  );
}

/**
 * The same loaders, split per kind, for a surface that drives
 * `CatalogItemPicker` itself rather than through `AttributionEditor`.
 */
export function useCatalogLoaders(
  subject?: AttributionSubject,
): Record<AttributionKind, CatalogLoader> {
  const load = useCatalogCandidates(subject);
  return React.useMemo(
    () =>
      Object.fromEntries(
        ATTRIBUTION_KINDS.map((kind) => [kind, (search: string) => load(kind, search)]),
      ) as Record<AttributionKind, CatalogLoader>,
    [load],
  );
}
